import { formatCLP } from "../lib/api";
import { Badge, type BadgeTone } from "./ui";

/**
 * Semáforo de deuda de un socio (auditoría PROP-01): al día / por vencer /
 * vencida, con el atraso en días o meses y el monto. El color acompaña al
 * texto, nunca lo reemplaza.
 *
 * Umbrales (criterio de cobranza SSR; lo usan Inicio, Morosidad y la cuenta
 * corriente):
 * hasta 1 mes recuperable (neutro), 2-3 meses gestión activa (ámbar),
 * 4 o más meses cobranza o corte (rose).
 */

export type NivelDeuda = "al-dia" | "por-vencer" | "vencida";

export interface EstadoDeuda {
  nivel: NivelDeuda;
  tono: BadgeTone;
  /** "Al día", "Por vencer", "Vencida hace 12 días", "Vencida hace 3 meses". */
  texto: string;
}

/**
 * Días desde el vencimiento más antiguo de una lista de fechas ISO
 * ("2026-08-29"). Negativo = todavía no vence; null = sin fechas.
 *
 * Es la ÚNICA forma de medir el atraso para el semáforo: la usan la cuenta
 * corriente (boletas pendientes) y Morosidad (vencimiento más antiguo que
 * manda el backend), así ambas pantallas dicen lo mismo de la misma socia.
 */
export function diasDesdeVencimiento(
  fechas: ReadonlyArray<string | null | undefined>,
  hoy: Date = new Date(),
): number | null {
  const primera = fechas
    .filter((f): f is string => !!f)
    .map((f) => f.slice(0, 10))
    .sort()[0];
  if (!primera) return null;
  const [a, m, d] = primera.split("-").map(Number);
  if (!a || !m || !d) return null;
  const venc = Date.UTC(a, m - 1, d);
  const ref = Date.UTC(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  return Math.round((ref - venc) / 86_400_000);
}

/** Meses de atraso que muestra el semáforo para `dias` (30 días = 1 mes).
 *  Sirve también para filtrar por "≥ meses" con el mismo criterio. */
export function mesesDeAtraso(dias: number | null | undefined): number {
  return dias != null && dias > 0 ? Math.floor(dias / 30) : 0;
}

/** Calcula nivel, tono y texto. `dias` (desde el vencimiento de la boleta
 *  más antigua impaga; negativo = aún no vence) manda sobre `meses` si viene. */
export function estadoDeuda(
  meses: number | null | undefined,
  monto: number,
  dias?: number | null,
): EstadoDeuda {
  if (monto <= 0) return { nivel: "al-dia", tono: "emerald", texto: "Al día" };

  if (dias != null) {
    if (dias <= 0) return { nivel: "por-vencer", tono: "slate", texto: "Por vencer" };
    const m = mesesDeAtraso(dias);
    if (m < 1) {
      return {
        nivel: "vencida",
        tono: "slate",
        texto: `Vencida hace ${dias} ${dias === 1 ? "día" : "días"}`,
      };
    }
    return { nivel: "vencida", tono: tonoPorMeses(m), texto: textoMeses(m) };
  }

  if (meses == null || meses <= 0) {
    return { nivel: "por-vencer", tono: "slate", texto: "Por vencer" };
  }
  return { nivel: "vencida", tono: tonoPorMeses(meses), texto: textoMeses(meses) };
}

function tonoPorMeses(meses: number): BadgeTone {
  if (meses >= 4) return "rose";
  if (meses >= 2) return "amber";
  return "slate";
}

function textoMeses(meses: number): string {
  if (meses < 12) return `Vencida hace ${meses} ${meses === 1 ? "mes" : "meses"}`;
  const anios = Math.floor(meses / 12);
  return `Vencida hace ${anios} ${anios === 1 ? "año" : "años"}`;
}

export function SemaforoDeuda({
  meses,
  monto,
  dias,
  mostrarMonto = true,
  className,
}: {
  /** Meses desde la boleta impaga más antigua (null = sin dato). */
  meses: number | null | undefined;
  /** Deuda total en CLP. 0 o menos = al día. */
  monto: number;
  /** Días desde el vencimiento de la boleta más antigua, si se conoce. */
  dias?: number | null | undefined;
  /** En tablas que ya tienen columna de monto, mostrar sólo el estado. */
  mostrarMonto?: boolean | undefined;
  className?: string | undefined;
}): JSX.Element {
  const e = estadoDeuda(meses, monto, dias);
  return (
    <Badge tone={e.tono} dot className={className}>
      <span>
        {e.texto}
        {mostrarMonto && monto > 0 && (
          <span className="tabular-nums"> · {formatCLP(monto)}</span>
        )}
      </span>
    </Badge>
  );
}
